import { LANDING_ASSETS, LANDING_ENROLL_STEPS } from "../constants/landing.constants";
import type { LandingCategory, LandingCourse } from "../types/landing.types";

export {
  formatInr,
  getCourseTax,
  getCourseTotal,
  getGstLabel,
} from "../utils/landingPricing";

export const LANDING_HERO = {
  badge: "Structured learning. Real progress",
  title: "Master Music with Expert Guidance",
  titleMobileLines: ["Invest With Confidence Grow", "Wealth With Clarity"] as const,
  subtitle:
    "Explore level-based classes in vocals and instruments, designed to help you grow step by step.",
};

const levelThumb = LANDING_ASSETS.levelThumbnail;

export const LANDING_CATEGORIES: LandingCategory[] = [
  {
    id: "cat-vocals",
    slug: "vocals",
    title: "Vocals",
    badge: "Classical",
    description:
      "Build a strong vocal foundation in Hindustani and Carnatic traditions, from swaras to ragas.",
    icon: LANDING_ASSETS.veenaHero,
    levels: [
      { id: "lv-1", slug: "hindustani-vocals", title: "Hindustani Vocals", thumbnail: levelThumb, categorySlug: "hindustani-vocals" },
      { id: "lv-2", slug: "carnatic-vocals", title: "Carnatic Vocals", thumbnail: levelThumb, categorySlug: "carnatic-vocals" },
      { id: "lv-3", slug: "raga-deep-dive", title: "Raga Deep Dive", thumbnail: levelThumb, categorySlug: "raga-deep-dive" },
    ],
  },
  {
    id: "cat-instrumental",
    slug: "instrumental",
    title: "Instrumental",
    badge: "Instrumental",
    description:
      "Learn guitar, keyboard, and piano with structured lessons for every skill level.",
    icon: LANDING_ASSETS.veenaSmall,
    levels: [
      { id: "lv-4", slug: "guitar", title: "Guitar", thumbnail: levelThumb, categorySlug: "guitar" },
      { id: "lv-5", slug: "keyboard", title: "Keyboard", thumbnail: levelThumb, categorySlug: "keyboard" },
      { id: "lv-6", slug: "piano", title: "Piano", thumbnail: levelThumb, categorySlug: "piano" },
    ],
  },
  {
    id: "cat-playback",
    slug: "playback-series",
    title: "Playback Series",
    badge: "Playback",
    description:
      "Master film and regional playback singing with practical, song-focused training.",
    icon: LANDING_ASSETS.heroGirlTanpura,
    levels: [
      { id: "lv-7", slug: "hindi-playback", title: "Hindi Playback Series", thumbnail: levelThumb, categorySlug: "hindi-playback" },
      { id: "lv-8", slug: "tamil-playback", title: "Tamil Playback Series", thumbnail: levelThumb, categorySlug: "tamil-playback" },
    ],
  },
  {
    id: "cat-children",
    slug: "music-for-children",
    title: "Music For Children",
    badge: "Children",
    description:
      "Engaging, age-appropriate music programs designed for young learners.",
    icon: LANDING_ASSETS.heroGirlTanpura,
    levels: [
      { id: "lv-9", slug: "grow-with-music", title: "Grow With Music", thumbnail: levelThumb, categorySlug: "grow-with-music" },
      { id: "lv-10", slug: "hindustani-children", title: "Hindustani Music to Children", thumbnail: levelThumb, categorySlug: "hindustani-children" },
      { id: "lv-11", slug: "mother-tongue", title: "Mother Tongue Series", thumbnail: levelThumb, categorySlug: "mother-tongue" },
      { id: "lv-12", slug: "lost-lullabies", title: "Lost Lullabies", thumbnail: levelThumb, categorySlug: "lost-lullabies" },
      { id: "lv-13", slug: "rhymes-to-swaras", title: "Rhymes To Swaras", thumbnail: levelThumb, categorySlug: "rhymes-to-swaras" },
    ],
  },
  {
    id: "cat-professional",
    slug: "professional",
    title: "Professional",
    badge: "Professional",
    description:
      "Advanced training for performers pursuing playback, contests, and stage readiness.",
    icon: LANDING_ASSETS.veenaHero,
    levels: [
      { id: "lv-14", slug: "voice-gym", title: "Voice Gym", thumbnail: levelThumb, categorySlug: "voice-gym" },
      { id: "lv-15", slug: "playback-singing", title: "Playback singing", thumbnail: levelThumb, categorySlug: "playback-singing" },
      { id: "lv-16", slug: "contest-prep", title: "Contest Preparation - Improving Voice Quality And Presentation", thumbnail: levelThumb, categorySlug: "contest-prep" },
    ],
  },
  {
    id: "cat-hobby",
    slug: "hobby-educational",
    title: "Hobby/ Educational",
    badge: "Hobby",
    description:
      "Explore Indian heritage and rhythm through relaxed, educational music courses.",
    icon: LANDING_ASSETS.veenaSmall,
    levels: [
      { id: "lv-17", slug: "heritage-series", title: "The Heritage Series", thumbnail: levelThumb, categorySlug: "heritage-series" },
      { id: "lv-18", slug: "language-of-rhythm", title: "Language Of Rhythm", thumbnail: levelThumb, categorySlug: "language-of-rhythm" },
    ],
  },
];

/** Mock-only school id for local checkout flow (no API call). */
export const MOCK_CHECKOUT_SCHOOL_ID =
  "00000000-0000-4000-8000-000000000001";

const BASE_COURSE: Omit<LandingCourse, "id" | "slug" | "categorySlug" | "audienceLabel"> = {
  title: "Foundations of Indian Classical Music",
  shortDescription:
    "This beginner-level course introduces the fundamentals of Indian classical music for learners with no prior experience.",
  longDescription:
    "This beginner-level course introduces the fundamentals of Indian classical music for learners with no prior experience. You will explore core concepts such as swaras, rhythm, melody, ragas, and talas, building listening skills and a strong conceptual foundation for further study.",
  thumbnail: LANDING_ASSETS.courseViolin,
  ageLabel: "10 years above",
  durationDays: 30,
  units: 4,
  videos: 20,
  minAge: 12,
  price: 5500,
  taxAmount: 400,
  schoolId: MOCK_CHECKOUT_SCHOOL_ID,
  benefits: [
    { title: "Build Musical Foundations", description: "Develop strong fundamentals with a clear learning approach" },
    { title: "Structured Lessons", description: "Learn through well-organized, progressive lessons" },
    { title: "Learning Journey", description: "4 structured lessons designed for guided music learning" },
    { title: "Self-Paced Learning", description: "Learn anytime, at your own pace" },
  ],
  enrollSteps: LANDING_ENROLL_STEPS,
};

const AUDIENCE_VARIANTS = [
  "10 years above",
  "10 Years above - Female",
  "10 years below - Male",
];

function buildCatalogCourses(): LandingCourse[] {
  const courses: LandingCourse[] = [];
  const allCategorySlugs = new Set<string>();

  for (const category of LANDING_CATEGORIES) {
    for (const level of category.levels) {
      allCategorySlugs.add(level.categorySlug);
    }
  }

  let index = 0;
  for (const categorySlug of allCategorySlugs) {
    for (let variant = 0; variant < 45; variant++) {
      const audience = AUDIENCE_VARIANTS[variant % AUDIENCE_VARIANTS.length];
      index += 1;
      const isPrimary =
        categorySlug === "hindustani-vocals" && variant === 0;
      courses.push({
        ...BASE_COURSE,
        id: `course-${index}`,
        slug: isPrimary
          ? "foundations-of-indian-classical-music"
          : `foundations-${categorySlug}-${index}`,
        categorySlug,
        audienceLabel: audience,
        title: isPrimary
          ? "Foundations of Indian Classical Music for Beginners – Beginner Level Course"
          : BASE_COURSE.title,
      });
    }
  }
  return courses;
}

export const LANDING_COURSES: LandingCourse[] = buildCatalogCourses();

export const CATEGORY_META: Record<string, { title: string; badge: string; heroImage: string }> = {
  "hindustani-vocals": { title: "Hindustani Vocals", badge: "Classical", heroImage: LANDING_ASSETS.veenaHero },
  "carnatic-vocals": { title: "Carnatic Vocals", badge: "Classical", heroImage: LANDING_ASSETS.veenaHero },
  "raga-deep-dive": { title: "Raga Deep Dive", badge: "Classical", heroImage: LANDING_ASSETS.veenaHero },
  guitar: { title: "Guitar", badge: "Instrumental", heroImage: LANDING_ASSETS.veenaSmall },
  keyboard: { title: "Keyboard", badge: "Instrumental", heroImage: LANDING_ASSETS.veenaSmall },
  piano: { title: "Piano", badge: "Instrumental", heroImage: LANDING_ASSETS.veenaSmall },
  "hindi-playback": { title: "Hindi Playback Series", badge: "Playback", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "tamil-playback": { title: "Tamil Playback Series", badge: "Playback", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "grow-with-music": { title: "Grow With Music", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "hindustani-children": { title: "Hindustani Music to Children", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "mother-tongue": { title: "Mother Tongue Series", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "lost-lullabies": { title: "Lost Lullabies", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "rhymes-to-swaras": { title: "Rhymes To Swaras", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "voice-gym": { title: "Voice Gym", badge: "Professional", heroImage: LANDING_ASSETS.veenaHero },
  "playback-singing": { title: "Playback singing", badge: "Professional", heroImage: LANDING_ASSETS.veenaHero },
  "contest-prep": { title: "Contest Preparation", badge: "Professional", heroImage: LANDING_ASSETS.veenaHero },
  "heritage-series": { title: "The Heritage Series", badge: "Hobby", heroImage: LANDING_ASSETS.veenaSmall },
  "language-of-rhythm": { title: "Language Of Rhythm", badge: "Hobby", heroImage: LANDING_ASSETS.veenaSmall },
  vocals: { title: "Vocals", badge: "Classical", heroImage: LANDING_ASSETS.veenaHero },
  instrumental: { title: "Instrumental", badge: "Instrumental", heroImage: LANDING_ASSETS.veenaSmall },
  "playback-series": { title: "Playback Series", badge: "Playback", heroImage: LANDING_ASSETS.heroGirlTanpura },
  "music-for-children": { title: "Music For Children", badge: "Children", heroImage: LANDING_ASSETS.heroGirlTanpura },
  professional: { title: "Professional", badge: "Professional", heroImage: LANDING_ASSETS.veenaHero },
  "hobby-educational": { title: "Hobby/ Educational", badge: "Hobby", heroImage: LANDING_ASSETS.veenaSmall },
};

export function isKnownCategorySlug(slug: string): boolean {
  if (LANDING_CATEGORIES.some((category) => category.slug === slug)) {
    return true;
  }
  return slug in CATEGORY_META;
}

function getCatalogSlugsForCategory(slug: string): string[] {
  const parent = LANDING_CATEGORIES.find((category) => category.slug === slug);
  if (parent) {
    return parent.levels.map(
      (level) => level.categorySlug ?? level.slug ?? level.id
    );
  }
  return [slug];
}

export function getCategoryMeta(slug: string) {
  const parent = LANDING_CATEGORIES.find((category) => category.slug === slug);
  if (parent) {
    return {
      title: parent.title,
      badge: parent.badge,
      heroImage: parent.icon,
    };
  }

  return (
    CATEGORY_META[slug] ?? {
      title: slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      badge: "Classical",
      heroImage: LANDING_ASSETS.veenaHero,
    }
  );
}

export function getCourseBySlug(categorySlug: string, courseSlug: string): LandingCourse | undefined {
  return LANDING_COURSES.find(
    (c) => c.categorySlug === categorySlug && c.slug === courseSlug
  );
}

export function getCoursesByCategory(categorySlug: string): LandingCourse[] {
  const slugs = new Set(getCatalogSlugsForCategory(categorySlug));
  return LANDING_COURSES.filter((course) => slugs.has(course.categorySlug));
}
